// src/pages/BinaryClassification.js
import React from "react";
import "./BinaryClassification.css";

const BinaryClassification = () => {
  return (
    <main className="binary">
      {/* Intro Section */}
      <section className="binary-intro">
        <h1 className="binary-title">Binary Classification Model</h1>
        <p className="binary-subtitle">
          In this section we summarize how we modeled the probability that an
          on-net shot becomes a goal (1) or not (0). We describe the data,
          features, model performance, and what the learned coefficients tell us
          about which factors actually move goal odds up or down.
        </p>
      </section>

      {/* Data & Features */}
      <section className="binary-section">
        <article className="binary-card">
          <h2 className="card-title">Research Question and Motivation</h2>
          <p className="card-text">
            The goal of this model is to isolate finishing skill and situational context after is shot is made on net. I wanted to find out what factors meaningfully contribute to the increase or decrease of goals.
          </p>
          <h2 className="card-title">Model Choice</h2>
          <p className="card-text">
  We used logistic regression as our primary binary classification model because it provides direct interpretability through coefficients, allowing us to quantify each feature. It seemed like the best fit to achieve our goal of finding high-impact factors. In binary classification when one class (goals) is rare, models tend to favor the majority class So we accounted for this in the design (correcting weights) </p>
        </article>
        <article className="binary-card">
          <h2 className="card-title">Data Filtering</h2>
          <p className="card-text">
We restricted the dataset to on-net shots only, so each row is either a shot that became a goal or a shot on target that did not score. After this filter, we had:          </p>
          <ul className="card-list">
            <li>
              <strong>78,891</strong> on-net shots remaining in the full
              dataset.
            </li>
          </ul>
          <p className="card-text">
            Focusing on on-net shots isolates the finishing and shot-quality
            problem: given that the puck is on target, what conditions make it
            more or less likely to go in?
          </p>
        </article>

        <article className="binary-card">
          <h2 className="card-title">Feature Set</h2>
          <p className="card-text">
            The model used the following <strong>19 input features</strong>:
          </p>
          <ul className="card-list">
            <li>arenaAdjustedShotDistance</li>
            <li>shotAngleAdjusted</li>
            <li>xCordAdjusted</li>
            <li>yCordAdjusted</li>
            <li>shotType</li>
            <li>shotRebound</li>
            <li>shotRush</li>
            <li>offWing</li>
            <li>
              defendingTeamAverageTimeOnIceOfDefencemenSinceFaceoff
            </li>
            <li>
              defendingTeamAverageTimeOnIceOfForwardsSinceFaceoff
            </li>
            <li>shooterTimeOnIceSinceFaceoff</li>
            <li>averageRestDifference</li>
            <li>period</li>
            <li>isHomeTeam</li>
            <li>timeSinceFaceoff</li>
            <li>timeSinceLastEvent</li>
            <li>distanceFromLastEvent</li>
            <li>ourGoaliePulled</li>
            <li>shotOnEmptyNet</li>
          </ul>
          <p className="card-text">
            These features combine shot geometry (distance, angle, location),
            game context (period, home/away, empty net), and fatigue-related
            signals (shift time, rest difference, time since faceoff/event).
          </p>
          <p className="card-text note">
            <strong>Model evolution:</strong> we treated{" "}
            <code>shotRebound</code> as a “cheat stat” (it encodes a lot of
            outcome-in-the-making information) and removed it from our final
            model to avoid overfitting to rebounds.
          </p>
        </article>
      </section>

      {/* Performance Section */}
      <section className="binary-section">
        <h2 className="section-title">Model Performance</h2>

        <article className="binary-card">
          <h3 className="card-title">Confusion Matrix</h3>
          <p className="card-text">
            On a held-out test set of <strong>19,723</strong> on-net shots:
          </p>

          <table className="confusion-table">
            <thead>
              <tr>
                <th></th>
                <th>Predicted 0</th>
                <th>Predicted 1</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th>Actual 0</th>
                <td>TN = 11,750</td>
                <td>FP = 5,866</td>
              </tr>
              <tr>
                <th>Actual 1</th>
                <td>FN = 605</td>
                <td>TP = 1,502</td>
              </tr>
            </tbody>
          </table>

          <ul className="card-list">
            <li>
              <strong>Accuracy:</strong> 0.672
            </li>
            <li>
              <strong>Precision (class 1):</strong> 0.204
            </li>
            <li>
              <strong>Recall (class 1):</strong> 0.713
            </li>
          </ul>
           <p className="card-text">
           True Positives (TP): correctly identified goals <br/>

False Positives (FP): predicted goals that did not score <br/>

False Negatives (FN): goals the model failed to predict <br/>
True Negative (TN): correctly predicted non-goals
          </p>

          <p className="card-text">
            Accuracy is the overall fraction of correct predictions. Precision
            for goals (1) is relatively low: when the model predicts “goal”, it
            is wrong most of the time. However,{" "}
            <strong>recall for goals is high (≈71%)</strong>, meaning the model
            successfully catches most of the actual goals.
          </p>
          <p className="card-text">
            This is typical for imbalanced problems where goals are rare: to
            catch most goals, the model must be more willing to predict “goal”
            and accept more false positives.
          </p>
        </article>

        <article className="binary-card">
          <h3 className="card-title">Class Metrics Summary</h3>
          <pre className="metrics-block">
{`Class 0 (No Goal)
  precision = 0.951
  recall    = 0.667
  f1-score  = 0.784
  support   = 17,616

Class 1 (Goal)
  precision = 0.204
  recall    = 0.713
  f1-score  = 0.317
  support   = 2,107

Overall accuracy        = 0.672
Macro avg F1            = 0.551
Weighted avg F1         = 0.734`}
          </pre>
          <p className="card-text">
            The model is very good at recognizing non-goals (high precision for
            class 0), and reasonably good at identifying most goals (recall for
            class 1). The trade-off is that many predicted goals are “false
            alarms”, which is expected when trying not to miss real goals.
          </p>
        </article>
      </section>

      {/* Feature Importance */}
      <section className="binary-section">
        <h2 className="section-title">Feature Importance & Insights</h2>

        <article className="binary-card">
          <h3 className="card-title">Most Influential Features by Weight</h3>
          <p className="card-text">
            The top features by absolute logistic regression weight were:
          </p>
          <pre className="metrics-block small">
{`arenaAdjustedShotDistance                2.055
xCordAdjusted                              1.014
shotOnEmptyNet                             0.678
shotType_SLAP                              0.243
shotType_SNAP                              0.232
shotAngleAdjusted                          0.134
defendingTeamAverageTimeOnIceOfForwards    0.134
shotType_WRAP                              0.106
averageRestDifference                      0.105
shotType_WRIST                             0.084
shotType_BACK                              0.084
shotType_TIP                               0.075
offWing                                    0.060
yCordAdjusted                              0.054
period                                     0.050
shotRebound                                0.045
isHomeTeam                                 0.028
timeSinceLastEvent                         0.027
shooterTimeOnIceSinceFaceoff               0.024
shotType_DEFL                              0.015`}
          </pre>
          <p className="card-text">
            <strong>Distance and x-coordinate</strong> dominate the model,
            which matches intuition: where the shot is taken from (both in
            terms of distance and lateral position) is the primary driver of
            scoring probability.
          </p>
        </article>

        <article className="binary-card">
          <h3 className="card-title">Factors Increasing Goal Odds</h3>
          <p className="card-text">
            Positive coefficients increase the log-odds of a goal. The top
            “goal-boosting” factors were:
          </p>
          <pre className="metrics-block small">
{`shotOnEmptyNet                             0.678
shotType_SLAP                              0.243
shotType_SNAP                              0.232
defendingTeamAverageTimeOnIceOfForwards    0.134
averageRestDifference                      0.105
shotType_WRIST                             0.084
offWing                                    0.060
yCordAdjusted                              0.054
period                                     0.050
isHomeTeam                                 0.028`}
          </pre>
          <p className="card-text">
            Some of these are straightforward:{" "}
            <strong>empty net shots</strong> are obviously more likely to go
            in, and harder shots (slap, snap, wrist) tend to be more dangerous.
          </p>
          <p className="card-text">
            Importantly,{" "}
            <strong>
              defendingTeamAverageTimeOnIceOfForwardsSinceFaceoff
            </strong>{" "}
            has a positive weight. This means that{" "}
            <strong>
              the longer the defending forwards have been on the ice, the higher
              the odds of a goal against
            </strong>
            . This supports our original theory that extended defensive shifts
            increase the chance of getting scored on.
          </p>
          <p className="card-text">
            <strong>averageRestDifference</strong> being positive suggests that
            when the shooting team is better rested than the opponent, their
            odds of finishing chances go up.
          </p>
        </article>

        <article className="binary-card">
          <h3 className="card-title">Factors Decreasing Goal Odds</h3>
          <p className="card-text">
            Negative coefficients decrease the log-odds of scoring:
          </p>
          <pre className="metrics-block small">
{`arenaAdjustedShotDistance   -2.055
xCordAdjusted               -1.014
shotAngleAdjusted           -0.133
shotType_WRAP               -0.106
shotType_BACK               -0.084
shotType_TIP                -0.075
shotRebound                 -0.045
shotType_DEFL               -0.015
ourGoaliePulled             -0.011
shotRush                    -0.005`}
          </pre>
          <p className="card-text">
            The strongest effect is{" "}
            <strong>arenaAdjustedShotDistance</strong>: as distance increases,
            scoring probability drops steeply. The negative coefficient for{" "}
            <strong>shotAngleAdjusted</strong> shows that worse angles also
            reduce goal odds, even after controlling for distance.
          </p>
          <p className="card-text">
            Some shot types (wraparounds, backhands, tips) carry negative
            coefficients after controlling for distance and angle, which
            suggests they are used in more difficult situations than their raw
            percentages might imply.
          </p>
          <p className="card-text">
            <strong>shotRebound</strong> appears with a small negative weight
            in this version of the model. However, we considered it a
            “cheating” feature because rebounds are inherently close and
            chaotic, and including a rebound flag essentially gives the model a
            shortcut. For fairness and generalization, we removed it in later
            models.
          </p>
        </article>
      </section>

      {/* Model Evolution / Takeaways */}
      <section className="binary-section">
        <h2 className="section-title">What We Learned</h2>

        <article className="binary-card">
          <h3 className="card-title">Model Design Choices</h3>
          <ul className="card-list">
            <li>
              Focusing on <strong>on-net shots</strong> turns the problem into
              “finish vs no finish” rather than “shot vs no shot”.
            </li>
            <li>
              We used a mix of{" "}
              <strong>geometry, fatigue, and context features</strong> to see if
              time-on-ice and rest matter after controlling for distance and
              angle.
            </li>
           
          </ul>
        </article>

        <article className="binary-card">
          <h3 className="card-title">Key Takeaways</h3>
          <ul className="card-list">
            <li>
              <strong>Distance and angle</strong> completely dominate scoring
              odds, which confirms the basic expected-goals intuition.
            </li>
            <li>
              <strong>Defensive fatigue matters:</strong> defending forwards&apos;
              time on ice since the faceoff has a clear, positive impact on goal
              odds against, supporting our original time-on-defense theory.
            </li>
            <li>
              <strong>Rest advantage helps:</strong> when the shooting team has
              more rest than the opponent, their finishing improves.
            </li>
            <li>
              <strong>Cheat stats are tempting but misleading:</strong> features
              like <code>shotRebound</code> can make the model look better on
              paper while actually encoding information that would not be
              available in a fair “pre-shot” setting, so we removed them in the
              final version.
            </li>
            <li>This helped show which factors matter most for goals, but as past research has found, distance and angle dominate, while context adds smaller effects and is limited by model assumptions and class imbalance.</li>
          </ul>
        </article>
      </section>
    </main>
  );
};

export default BinaryClassification;
